import { ApolloQueryResult } from '@apollo/client';
import merge from 'lodash/merge';
import { all, call, put, select } from 'redux-saga/effects';

import {
  fetchTopLevelCustomEvents,
  getBuiltInEvents,
  getCampaigns,
  getContactProperties,
  getFilterProperties,
  getMessageChannels,
  getMessageTypes,
  getSendNodeResources,
  getWorkflows,
  getWorkflowWebhooks,
} from 'api/WorkflowsAPI';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { NodeData, NodeType } from 'modules/workflows/types';
import { getErrorResponse } from 'utils/xhr';
import {
  NODE_TYPES,
  WORKFLOW_ACTIONS,
  WORKFLOW_FILTERS,
  WORKFLOW_NODE_TYPES,
  WORKFLOW_TRIGGERS,
} from '../../constants';
import { actions, OpenNodeRequestAction } from '../actions';
import { getNodeById } from '../selectors';
import { createChildResourceEffect } from './sagaUtils';

/**
 * Not all required data is available through GraphQL yet, so this function combines
 * GraphQL and old API data into a single resources object keyed on the top level by
 * resource name.
 */
const parseResources = (resources: Record<string, any>) =>
  Object.entries(resources).reduce((acc, [resourceKey, resourceValue]) => {
    return resourceKey === 'gql'
      ? { ...acc, ...resourceValue.data }
      : { ...acc, [resourceKey]: resourceValue };
  }, {});

interface FetchSendNodeResource {
  campaignId: number;
  channel: string;
}

/*
 * Based on the node type, collects required node resources as redux-saga
 *  effects and merges data into single response object
 */

function fetchDefaultResources(nodeData: NodeData) {
  /* eslint-disable redux-saga/no-unhandled-errors */
  const { nodeClass: nodeName, campaignId } = nodeData;

  const fetchSendNodeResources = (args: FetchSendNodeResource) => {
    return call(
      getSendNodeResources as (arg: FetchSendNodeResource) => Promise<ApolloQueryResult<any>>,
      args,
    );
  };

  switch (nodeName) {
    case WORKFLOW_ACTIONS.SEND_EMAIL:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'email' }),
      });
    case WORKFLOW_ACTIONS.SEND_EMBEDDED:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'embedded' }),
      });
    case WORKFLOW_ACTIONS.SEND_PUSH:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'push' }),
      });
    case WORKFLOW_ACTIONS.SEND_IN_APP:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'inApp' }),
      });
    case WORKFLOW_ACTIONS.SEND_SMS:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'sms' }),
      });
    case WORKFLOW_ACTIONS.SEND_WEB_PUSH:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'webPush' }),
      });
    case WORKFLOW_ACTIONS.SEND_WHATSAPP:
      return all({
        gql: fetchSendNodeResources({ campaignId, channel: 'whatsApp' }),
      });
    case WORKFLOW_ACTIONS.CHANNEL_OPT: {
      const { childNodes = [] } = nodeData || {};
      if (childNodes.length <= 0) {
        return call(() => null);
      }
      return all(
        childNodes.reduce((resourceEffects, childNode) => {
          return {
            ...resourceEffects,
            [childNode.type]: all(
              createChildResourceEffect(childNode, call, fetchTopLevelCustomEvents),
            ),
          };
        }, {}),
      );
    }
    case WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION:
      return call(getMessageChannels);
    case WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE:
      return call(getMessageTypes);
    case WORKFLOW_ACTIONS.CALL_WEBHOOK:
      return call(getWorkflowWebhooks);
    case WORKFLOW_ACTIONS.DELAY: // es data
      return all({
        contactProperties: call(getContactProperties),
        customEvents: call(fetchTopLevelCustomEvents),
        builtInEvents: call(getBuiltInEvents),
      });
    case WORKFLOW_ACTIONS.TRIGGER_WORKFLOW:
      return call(getWorkflows);
    /* requires no additional data */
    case WORKFLOW_ACTIONS.CHANGE_CONTACT_FIELD:
    case WORKFLOW_ACTIONS.UPDATE_CONTACT_WEBHOOK:
    default:
      return null;
  }
  /* eslint-enable redux-saga/no-unhandled-errors */
}

function fetchFilterResources(nodeData: NodeData) {
  /* eslint-disable redux-saga/no-unhandled-errors */
  const { nodeClass: filterType } = nodeData;
  switch (filterType) {
    case WORKFLOW_FILTERS.FIELDS_MATCH: // es data
      return all({
        contactProperties: call(getContactProperties),
        builtInEvents: call(getBuiltInEvents),
      });
    case WORKFLOW_FILTERS.ATTRIBUTE_SPLIT: // es data
      return call(getContactProperties);
    case WORKFLOW_FILTERS.AB_SPLIT: // requires no additional data
    default:
      return null;
  }
  /* eslint-enable redux-saga/no-unhandled-errors */
}

function fetchStartTriggerResources(nodeData: NodeData) {
  /* eslint-disable redux-saga/no-unhandled-errors */
  const { nodeClass: triggerType } = nodeData;
  switch (triggerType) {
    case WORKFLOW_TRIGGERS.SCHEDULED_RECURRING_LIST:
    case WORKFLOW_TRIGGERS.UNSUBSCRIBED_FROM_MESSAGE_TYPE:
      return call(getMessageTypes);
    case WORKFLOW_TRIGGERS.UNSUBSCRIBED_FROM_CHANNEL:
      return call(getMessageChannels);
    case WORKFLOW_TRIGGERS.FIELD_CHANGED: // es data
      return all({
        contactProperties: call(getContactProperties),
        filterProperties: call(getFilterProperties),
      });
    case WORKFLOW_TRIGGERS.OPENED_AN_EMAIL:
    case WORKFLOW_TRIGGERS.CLICKED_AN_EMAIL:
    case WORKFLOW_TRIGGERS.OPENED_PUSH:
    case WORKFLOW_TRIGGERS.CLICKED_AN_IN_APP:
    case WORKFLOW_TRIGGERS.CLOSED_AN_IN_APP:
    case WORKFLOW_TRIGGERS.DELETED_AN_IN_APP:
    case WORKFLOW_TRIGGERS.DELIVERED_AN_IN_APP:
    case WORKFLOW_TRIGGERS.OPENED_AN_IN_APP:
      return call(getCampaigns);
    case WORKFLOW_TRIGGERS.RECEIVED_API: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.PURCHASED: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.RECEIVED_AN_SMS: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.CLICKED_AN_SMS: // es data
      return call(getFilterProperties);
    case WORKFLOW_TRIGGERS.TRIGGERED_BY_WORKFLOW: // requires no additional data
    default:
      return null;
  }
  /* eslint-enable redux-saga/no-unhandled-errors */
}

function parseResourcesWithDefault(resources: Record<string, any> | null) {
  // fetch and combine resources from old API
  return resources ? resources?.data || parseResources(resources) : [];
}

const getNodeOverrides = (
  nodeType: NodeType,
  resources: Record<string, any> | null,
  nodeData: NodeData,
  nodeArgs: OpenNodeRequestAction['payload']['nodeArgs'],
) => {
  let updatedChildNodes = nodeData?.childNodes || [];
  switch (nodeType) {
    case WORKFLOW_ACTIONS.CHANNEL_OPT:
      if (resources) {
        updatedChildNodes = (nodeData?.childNodes || []).map((childNode) => {
          const sendNodeResource = resources[childNode.type];
          if (!sendNodeResource?.campaign?.data) {
            return childNode;
          }
          return merge(
            {
              nodeData: {
                resource: {
                  ...sendNodeResource.campaign.data,
                },
              },
            },
            childNode,
          );
        });
      }
      return {
        nodeData: {
          ...nodeData,
          selectedEditor: nodeArgs?.childNodeData?.type ?? NODE_TYPES.CHANNEL_OPT,
          childNodes: updatedChildNodes,
        },
      };
    default:
      return {};
  }
};

export default function* openNodeSaga({ payload }: OpenNodeRequestAction) {
  try {
    const { nodeId, nodeArgs } = payload;
    const { nodeData } = yield select(getNodeById, nodeId);
    const { nodeSuperClass: workflowType, nodeClass: nodeType } = nodeData;

    let resources: Record<string, any> | null = null;
    // takes redux-saga effects and yields required resource data
    switch (workflowType) {
      case WORKFLOW_NODE_TYPES.TRIGGER:
        resources = yield fetchStartTriggerResources(nodeData);
        break;
      case WORKFLOW_NODE_TYPES.FILTER:
        resources = yield fetchFilterResources(nodeData);
        break;
      case WORKFLOW_NODE_TYPES.ACTION:
        resources = yield fetchDefaultResources(nodeData);
        break;
      default:
        throw new Error('Unknown workflow node type');
    }
    const nodeOverrides = getNodeOverrides(nodeType, resources, nodeData, nodeArgs);
    // fetch and combine resources from old API
    const parsedResources = parseResourcesWithDefault(resources);
    yield put(actions.openNodeResponse(parsedResources, nodeId, nodeOverrides));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.openNodeError(err));
    const message = `Error opening node. Please try again later.`;
    yield put(addNegativeToastWithErrorId(message, 'openNodeSaga'));
  }
}
